/**
 * Query-key convention for WingWise.
 *
 * ALL workspace-scoped data MUST be keyed by workspace_id (and user_id where
 * relevant) so cache isolation is impossible to accidentally break when a
 * user switches workspaces or logs out.
 *
 * Filter objects are normalised to sorted key/value pairs to avoid subtle
 * cache misses caused by property-order differences.
 *
 * Reserved top-level namespaces:
 *   "workspace"   — workspace-scoped
 *   "public"      — cross-workspace (rare — health checks, calendar info)
 */

function _current() {
  let ws, uid;
  try {
    const raw = localStorage.getItem("tm_user");
    if (raw) {
      const u = JSON.parse(raw);
      ws = u?.workspace_id || u?.workspace?.id;
      uid = u?.id || u?.user_id;
    }
  } catch { /* ignore */ }
  return { workspace_id: ws || "anon", user_id: uid || "anon" };
}

function _normFilters(filters) {
  if (!filters || typeof filters !== "object") return {};
  return Object.keys(filters)
    .sort()
    .reduce((acc, k) => {
      const v = filters[k];
      if (v !== undefined && v !== null && v !== "") acc[k] = v;
      return acc;
    }, {});
}

/**
 * @param {string[]} rest — key path segments after workspace/user prefix
 * @param {object}   opts — { userScoped, filters, version }
 */
export function qk(rest, opts = {}) {
  const { workspace_id, user_id } = _current();
  const parts = ["workspace", workspace_id];
  if (opts.userScoped !== false) {
    parts.push("user", user_id);
  }
  for (const p of rest) parts.push(p);
  if (opts.filters !== undefined) parts.push(_normFilters(opts.filters));
  if (opts.version !== undefined) parts.push({ v: opts.version });
  return parts;
}

// -------- Canonical key builders (import these from callers) --------
export const keys = {
  // opportunities (per-user because Saved Views + user-specific state)
  opportunities: (filters) => qk(["opportunities"], { filters }),
  opportunityDetail: (symbol) => qk(["opportunity-detail", symbol?.toUpperCase()]),
  dailyBrief: () => qk(["daily-brief"]),

  // analysis
  analysisHistory: (filters) => qk(["analysis", "history"], { filters }),
  analysis: (id) => qk(["analysis", id]),
  analysisExisting: (symbol) => qk(["analysis", "existing", symbol?.toUpperCase()]),

  // proposal
  proposal: (id) => qk(["proposal", id]),
  proposalVersions: (id) => qk(["proposal", id, "versions"]),
  sizing: (params) => qk(["risk", "sizing"], { filters: params }),
  concentration: (symbol, portfolioKey) => qk(["risk", "concentration", symbol, portfolioKey]),

  // portfolios (shared across the workspace, but scoped per user for view state)
  portfolios: () => qk(["portfolios"], { userScoped: false }),
  portfolioSparkline: (id, range) => qk(["portfolios", "sparkline", id, range], { userScoped: false }),

  // positions
  positions: (filter = "open") => qk(["positions", filter]),

  // market intel
  bars: (symbol, timeframe, limit) => qk(["market", "bars", symbol?.toUpperCase(), timeframe, limit], { userScoped: false }),
  news: (symbol) => qk(["market-intel", "news", symbol?.toUpperCase()], { userScoped: false }),
  fundamentals: (symbol) => qk(["market-intel", "fundamentals", symbol?.toUpperCase()], { userScoped: false }),
  technicals: (symbol) => qk(["market-intel", "technicals", symbol?.toUpperCase()], { userScoped: false }),
  marketMemory: (symbol) => qk(["market-intel", "memory", symbol?.toUpperCase()]),

  // regime
  regime: () => qk(["regime"], { userScoped: false }),
  regimeHistory: (days) => qk(["regime", "history", days], { userScoped: false }),

  // coaching
  coaching: (filters) => qk(["coaching", "insights"], { filters }),

  // notes
  notes: (entityType, entityId) => qk(["notes", entityType, entityId]),

  // saved views
  savedViews: () => qk(["opportunity-views"]),

  // replay
  replayList: () => qk(["replay", "list"]),
  replay: (id) => qk(["replay", id]),

  // search
  searchRecent: () => qk(["search", "recent"]),
};
